import { useMemo } from 'react';
import { Specs } from '@doxyme/extensions-bridge-react';
import { useLocale } from '@doxyme/locale';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import {
  getUsername,
  getRole,
  getUserState,
  getUserPicture,
} from '../../../features/realtime/realtime.selectors';
import { useAppSelector } from '../../useReduxToolkit';
import { usePersonalRoom } from '../../usePersonalRoom';
import { useThemeNameByRoute } from '../../useThemeNameByRoute';
import { useUserSubscription } from '../../useUserSubscription';
import { useGetUserId } from '../../../hooks/useCredentials';
import { useEntitlementsActions } from '@frontegg/nextjs';
import { Permission, useUserPermissions } from '../../useAuth';
import { usePatientWaitingRoom } from '../../../views/waiting-room/waitingRoomView/hooks/usePatientWaitingRoom/usePatientWaitingRoom';
import { ProviderFeatureKey } from '../../../services/graphQlTypes.generated';
import { getLogger } from '../../../libs/logger';

const logger = getLogger('useExtensionContextFeaturesAndPermissions');

type Role = Specs.ReceiveContext.Role;

type EntitlementCheck = {
  isEntitled: boolean;
  appFeature: Specs.ReceiveContext.FronteggExtensionsFeatureKeys;
};

function featuresFromEntitlements(
  entitlementChecks: EntitlementCheck[],
): Specs.ReceiveContext.FronteggExtensionsFeatureKeys[] {
  return entitlementChecks.filter((c) => c.isEntitled).map((c) => c.appFeature);
}

function filterPermissions(
  permissions: Permission[],
): Specs.ReceiveContext.FronteggExtensionsPermissionKeys[] {
  return Object.values(
    Specs.ReceiveContext.FronteggExtensionsPermissionKeys,
  ).filter((p) => {
    return permissions.some((permission) => permission.key === p);
  });
}

function useExtensionContextFeaturesAndPermissions(): {
  features: Specs.ReceiveContext.FronteggExtensionsFeatureKeys[];
  permissions: Specs.ReceiveContext.FronteggExtensionsPermissionKeys[];
} {
  // We use useEntitlementsActions and useUserPermissions directly here
  // instead of useFeatureEntitlements because FronteggExtensionsFeatureKeys are
  // extension-only keys that should not be exposed through the frontend context
  // type — only the bridge should ever check them against Frontegg.
  const { isEntitledTo } = useEntitlementsActions();
  const { permissions } = useUserPermissions();

  return useMemo(() => {
    const entitlementChecks: EntitlementCheck[] = Object.values(
      Specs.ReceiveContext.FronteggExtensionsFeatureKeys,
    ).map((appFeature) => {
      try {
        return {
          appFeature,
          isEntitled:
            isEntitledTo({ featureKey: appFeature })?.isEntitled ?? false,
        };
      } catch (error) {
        // Catch per-key so a single failing entitlement (e.g. a key Frontegg
        // no longer recognises after a rename/removal) cannot wipe out the
        // entire features array. Fall back to "not entitled" for that key so
        // we never grant access we can't verify.
        logger.error(
          `Failed to resolve Frontegg entitlement for extensions feature "${appFeature}"`,
          error,
        );
        return { appFeature, isEntitled: false };
      }
    });

    return {
      features: featuresFromEntitlements(entitlementChecks),
      permissions: filterPermissions(permissions),
    };
  }, [isEntitledTo, permissions]);
}

export type UseExtensionsReceiveContextBaseReturnType = {
  role?: Role;
  username?: string;
  avatar?: string;
  clinicId?: string;
  locale?: string;
  theme?: string;
  sessionId?: string;
};

export type UseExtensionsReceiveContextProviderReturnType = {
  personalRoomUrl?: string;
  subscriptionType?: string;
  userId?: string;
  features?: Specs.ReceiveContext.FronteggExtensionsFeatureKeys[];
  permissions?: Specs.ReceiveContext.FronteggExtensionsPermissionKeys[];
};

export type UseExtensionsReceiveContextPatientReturnType = {
  ip?: string;
  features?: Specs.ReceiveContext.FronteggExtensionsFeatureKeys[];
};

/**
 * Hook responsible for providing context to the Extensions SDK, will be more data later
 */
export function useExtensionsReceiveContextBase(): UseExtensionsReceiveContextBaseReturnType {
  const { institutionId: clinicId, sessionId } = useAppSelector(
    getApolloSessionDetails,
  );
  const username = useAppSelector(getUsername);
  const role = useAppSelector(getRole);
  const avatar = useAppSelector(getUserPicture);
  const { locale } = useLocale();
  const theme = useThemeNameByRoute();

  const context = useMemo(() => {
    return {
      username,
      avatar,
      clinicId,
      role: role as Role | undefined,
      locale,
      theme,
      sessionId: Boolean(sessionId) ? sessionId : undefined,
    };
  }, [username, avatar, clinicId, role, locale, theme, sessionId]);

  return context;
}

/**
 * Hook responsible for providing provider context to the Extensions SDK, will be more data later
 */
export function useExtensionsReceiveContextProvider(): UseExtensionsReceiveContextProviderReturnType {
  const { roomUrl: personalRoomUrl } = usePersonalRoom();
  const { plan } = useUserSubscription();
  const userId = useGetUserId();
  const { features, permissions } = useExtensionContextFeaturesAndPermissions();

  return useMemo(
    () => ({
      personalRoomUrl,
      subscriptionType: plan,
      userId: userId || undefined,
      features,
      permissions,
    }),
    [personalRoomUrl, plan, userId, features, permissions],
  );
}

/**
 * Hook responsible for providing patient context to the Extensions SDK, will be more data later
 */
export function useExtensionsReceiveContextPatient(): UseExtensionsReceiveContextPatientReturnType {
  const ip = useAppSelector(getUserState)?.privateData?.ip;
  const { room } = usePatientWaitingRoom();

  const context = useMemo(() => {
    const features = mapRoomFeaturesToContextFeatures(
      room?.providerEntitlements?.features,
    );

    return {
      ip,
      features,
    };
  }, [ip, room?.providerEntitlements?.features]);

  return context;
}

const mapRoomFeaturesToContextFeatures = (
  features?: ProviderFeatureKey[],
): Specs.ReceiveContext.FronteggExtensionsFeatureKeys[] | undefined => {
  if (!features || features.length === 0) {
    return undefined;
  }

  // ProviderFeatureKey is derived from FronteggExtensionsFeatureKeys:
  // the GraphQL enum exposes member NAMES (e.g. 'ProPlan') while the
  // SDK enum exposes Frontegg feature strings (e.g. 'pro-plan-feature').
  // Since the TS keys align by construction, look each GraphQL value up
  // against the SDK enum's own keys. `features: string[]` because every
  // string enum value is already a string; no further cast needed.
  const featureNames: readonly string[] = features;
  const result: Specs.ReceiveContext.FronteggExtensionsFeatureKeys[] = [];
  for (const [key, value] of Object.entries(
    Specs.ReceiveContext.FronteggExtensionsFeatureKeys,
  )) {
    if (featureNames.includes(key)) {
      result.push(value);
    }
  }
  return result;
};
